'use client';

/**
 * localStorage-backed deck persistence.
 *
 * Decks live under a single key (`photoslop.decks`) as a record keyed by
 * deck id. Storage is best-effort: corrupt blobs are tossed and the caller
 * is handed an empty state rather than a crash.
 */

import type { Deck, DeckSummary } from './types';

const STORAGE_KEY = 'photoslop.decks.v1';

type DeckStore = Record<string, Deck>;

function readStore(): DeckStore {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as DeckStore;
  } catch {
    return {};
  }
}

function writeStore(store: DeckStore): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Quota exceeded or serialisation failure — silently drop.
    // The editor autosaves on every mutation so the next attempt will retry.
  }
}

export function listDecks(): DeckSummary[] {
  const store = readStore();
  return Object.values(store)
    .map((d) => ({
      id: d.id,
      name: d.name,
      slideCount: d.slides.length,
      updatedAt: d.updatedAt,
      previews: d.slides
        .slice(0, 4)
        .map((s) => s.bg?.src ?? '')
        .filter(Boolean),
    }))
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export function loadDeck(id: string): Deck | null {
  const store = readStore();
  return store[id] ?? null;
}

export function saveDeck(deck: Deck): Deck {
  const store = readStore();
  const next: Deck = { ...deck, updatedAt: Date.now() };
  store[deck.id] = next;
  writeStore(store);
  return next;
}

export function deleteDeck(id: string): void {
  const store = readStore();
  if (!(id in store)) return;
  delete store[id];
  writeStore(store);
}

export function createId(prefix = 'id'): string {
  // crypto.randomUUID is available in all modern browsers & Node 19+.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}
